export type ViewportMode = "desktop" | "mobileLandscape" | "mobilePortrait";

export interface MobileUiController {
  getViewportMode(): ViewportMode;
  subscribeViewportMode(listener: (mode: ViewportMode) => void): () => void;
  destroy(): void;
}

const TOUCH_LAYOUT_SHORT_EDGE_MAX = 1024;
const VIEWPORT_MODE_SETTLE_DELAYS_MS = [60, 180, 360] as const;

export function createMobileUiController(root: HTMLElement): MobileUiController {
  let viewportMode = detectViewportMode();
  const viewportListeners = new Set<(mode: ViewportMode) => void>();
  const timerIds: number[] = [];
  const visualViewport = window.visualViewport;

  const layer = document.createElement("div");
  layer.className = "mobile-ui";

  const rotateOverlay = document.createElement("div");
  rotateOverlay.className = "mobile-ui__rotate";

  const rotateCard = document.createElement("div");
  rotateCard.className = "mobile-ui__rotate-card";

  const rotateTitle = document.createElement("h1");
  rotateTitle.className = "mobile-ui__rotate-title";
  rotateTitle.textContent = "Rotate To Play";

  const rotateCopy = document.createElement("p");
  rotateCopy.className = "mobile-ui__rotate-copy";
  rotateCopy.textContent =
    "Polarity plays in landscape. Rotate your device to continue.";

  rotateCard.append(rotateTitle, rotateCopy);
  rotateOverlay.append(rotateCard);
  layer.append(rotateOverlay);
  root.append(layer);

  applyViewportClasses();
  syncUi();

  const updateViewportMode = () => {
    const nextMode = detectViewportMode();
    if (nextMode === viewportMode) {
      applyViewportClasses();
      syncUi();
      return;
    }

    viewportMode = nextMode;
    applyViewportClasses();
    syncUi();
    for (const listener of viewportListeners) {
      listener(viewportMode);
    }
  };

  const clearTimers = () => {
    while (timerIds.length > 0) {
      const timerId = timerIds.pop();
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    }
  };

  const scheduleViewportModeUpdate = () => {
    clearTimers();
    updateViewportMode();

    for (const delay of VIEWPORT_MODE_SETTLE_DELAYS_MS) {
      timerIds.push(window.setTimeout(updateViewportMode, delay));
    }
  };

  window.addEventListener("resize", scheduleViewportModeUpdate, { passive: true });
  window.addEventListener("orientationchange", scheduleViewportModeUpdate);
  visualViewport?.addEventListener("resize", scheduleViewportModeUpdate, { passive: true });
  visualViewport?.addEventListener("scroll", scheduleViewportModeUpdate, { passive: true });

  return {
    getViewportMode(): ViewportMode {
      return viewportMode;
    },
    subscribeViewportMode(listener: (mode: ViewportMode) => void): () => void {
      viewportListeners.add(listener);
      return () => {
        viewportListeners.delete(listener);
      };
    },
    destroy(): void {
      clearTimers();
      window.removeEventListener("resize", scheduleViewportModeUpdate);
      window.removeEventListener("orientationchange", scheduleViewportModeUpdate);
      visualViewport?.removeEventListener("resize", scheduleViewportModeUpdate);
      visualViewport?.removeEventListener("scroll", scheduleViewportModeUpdate);
      root.classList.remove("app-mobile", "app-mobile-landscape", "app-mobile-portrait");
      root.removeAttribute("data-viewport-mode");
      layer.remove();
    },
  };

  function applyViewportClasses(): void {
    root.classList.toggle("app-mobile", viewportMode !== "desktop");
    root.classList.toggle("app-mobile-landscape", viewportMode === "mobileLandscape");
    root.classList.toggle("app-mobile-portrait", viewportMode === "mobilePortrait");
    root.dataset.viewportMode = viewportMode;
  }

  function syncUi(): void {
    rotateOverlay.hidden = viewportMode !== "mobilePortrait";
  }
}

function detectViewportMode(): ViewportMode {
  const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const touchCapable = coarsePointer || navigator.maxTouchPoints > 0;
  const shortEdge = Math.min(viewportWidth, viewportHeight);
  const useTouchLayout =
    coarsePointer || (touchCapable && shortEdge <= TOUCH_LAYOUT_SHORT_EDGE_MAX);

  if (!useTouchLayout) {
    return "desktop";
  }

  return viewportWidth >= viewportHeight
    ? "mobileLandscape"
    : "mobilePortrait";
}
